import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { NAV_LINKS } from "@/lib/site";
import { fetchCourses } from "@/lib/coursesApi";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [courses, setCourses] = useState([]);
  const location = useLocation();

  useEffect(() => {
    fetchCourses().then((c) => setCourses(c || [])).catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const linkCls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors duration-200 ${
      isActive ? "text-orange" : "text-white/75 hover:text-white"
    }`;

  const highlightCls =
    "relative inline-flex items-center rounded-full bg-orange px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(255,90,0,0.6)] transition-colors duration-200 hover:bg-orange/90";

  const highlightMobileCls =
    "mb-2 inline-flex items-center justify-center rounded-full bg-orange px-5 py-3 text-base font-semibold text-white";

  const renderDesktopLink = (l) => {
    if (l.dropdown === "courses") {
      return (
        <div key="courses-dd" className="group relative" data-testid="nav-courses-dropdown">
          <NavLink to={l.to} data-testid="nav-courses" className={linkCls}>
            <span className="inline-flex items-center gap-1">{l.label}<ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" /></span>
          </NavLink>
          {courses.length > 0 ? (
            <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-white/10 bg-navy p-2 shadow-2xl">
                {courses.slice(0, 8).map((c) => (
                  <NavLink key={c.id} to={`/courses/${c.slug}`} data-testid={`nav-course-${c.slug}`} className="block rounded-lg px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white">
                    {c.name}
                  </NavLink>
                ))}
                <NavLink to="/courses" data-testid="nav-view-all-courses" className="mt-1 block rounded-lg border-t border-white/10 px-3 py-2 text-sm font-semibold text-orange">
                  View All Courses →
                </NavLink>
              </div>
            </div>
          ) : (
            <div className="invisible absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="rounded-xl border border-white/10 bg-navy p-2 shadow-2xl">
                <NavLink to="/courses" data-testid="nav-view-all-courses" className="block rounded-lg px-3 py-2 text-sm font-semibold text-orange">
                  View All Courses →
                </NavLink>
              </div>
            </div>
          )}
        </div>
      );
    }
    if (l.external) {
      return (
        <a
          key={l.href}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`nav-${l.label.toLowerCase()}`}
          className={l.highlight ? highlightCls : linkCls({ isActive: false })}
        >
          {l.label}
        </a>
      );
    }
    return (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={`nav-${l.to}`}
        className={l.highlight ? () => highlightCls : linkCls}
      >
        {l.label}
      </NavLink>
    );
  };

  const renderMobileLink = (l, i) => (
    <motion.div
      key={l.to || l.href}
      initial={{ y: 12 }}
      animate={{ y: 0 }}
      transition={{ delay: 0.03 * i, duration: 0.2, ease: "easeOut" }}
    >
      {l.dropdown === "courses" ? (
        <>
          <NavLink
            to={l.to}
            data-testid="mobile-nav-/courses"
            className={({ isActive }) => `block py-4 font-heading text-3xl ${isActive ? "text-orange" : "text-white"}`}
          >
            {l.label}
          </NavLink>
          {courses.length > 0 && (
            <div className="mb-2 flex flex-col gap-1 border-b border-white/10 pb-3 pl-1">
              {courses.slice(0, 8).map((c) => (
                <NavLink key={c.id} to={`/courses/${c.slug}`} data-testid={`mobile-nav-course-${c.slug}`} className="py-1.5 text-base text-white/70 hover:text-white">
                  {c.name}
                </NavLink>
              ))}
              <NavLink to="/courses" className="py-1.5 text-base font-semibold text-orange">View All Courses →</NavLink>
            </div>
          )}
          {courses.length === 0 && <div className="border-b border-white/10" />}
        </>
      ) : l.external ? (
        <a
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`mobile-nav-${l.label.toLowerCase()}`}
          className={
            l.highlight
              ? highlightMobileCls
              : "block border-b border-white/10 py-4 font-heading text-3xl text-white"
          }
        >
          {l.label}
        </a>
      ) : (
        <NavLink
          to={l.to}
          data-testid={`mobile-nav-${l.to}`}
          className={({ isActive }) =>
            l.highlight
              ? highlightMobileCls
              : `block border-b border-white/10 py-4 font-heading text-3xl ${isActive ? "text-orange" : "text-white"}`
          }
        >
          {l.label}
        </NavLink>
      )}
    </motion.div>
  );

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled ? "glass-dark border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-asai flex h-[70px] items-center justify-between gap-4">
        <Logo />

        <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
          {NAV_LINKS.map(renderDesktopLink)}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
          onClick={() => setOpen(true)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white xl:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            style={{ backgroundColor: "rgb(6, 15, 35)" }}
            className="fixed inset-0 z-[70] flex flex-col xl:hidden"
          >
            {/* Explicit solid backdrop — guarantees the drawer is never translucent, no matter what sits behind */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 -z-10"
              style={{ backgroundColor: "rgb(6, 15, 35)" }}
            />
            <div className="container-asai flex h-[70px] items-center justify-between">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="container-asai flex flex-1 flex-col gap-1 overflow-y-auto pt-6 pb-10">
              {NAV_LINKS.map(renderMobileLink)}
              <a
                href="tel:+917830060800"
                className="mt-6 inline-flex items-center gap-2 text-sm text-white/70"
              >
                <Phone className="h-4 w-4 text-orange" /> +91 78300 60800
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
